import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { type AutoSaveStatus, formatLastSaved } from '../hooks/useAutoSave'

interface AutoSaveIndicatorProps {
  status: AutoSaveStatus
  lastSaved: Date | null
  className?: string
}

/**
 * Visual indicator for auto-save status.
 * Shows saving spinner, success checkmark, or error icon.
 */
const AutoSaveIndicator: React.FC<AutoSaveIndicatorProps> = ({
  status,
  lastSaved,
  className,
}) => {
  if (status === 'idle' && !lastSaved) {
    return null
  }

  const getIcon = () => {
    switch (status) {
      case 'saving':
        return <i className="fa-solid fa-spinner fa-spin" />
      case 'saved':
        return <i className="fa-solid fa-check" />
      case 'error':
        return <i className="fa-solid fa-exclamation-triangle" />
      default:
        return <i className="fa-solid fa-cloud" />
    }
  }

  const getText = () => {
    switch (status) {
      case 'saving':
        return 'Saving...'
      case 'saved':
        return formatLastSaved(lastSaved)
      case 'error':
        return 'Save failed'
      default:
        return formatLastSaved(lastSaved)
    }
  }

  const getStatusStyle = () => {
    switch (status) {
      case 'saving':
        return styles.saving
      case 'saved':
        return styles.saved
      case 'error':
        return styles.error
      default:
        return styles.idle
    }
  }

  return (
    <div
      className={`${css(styles.container, getStatusStyle())} ${className || ''}`}
    >
      <span className={css(styles.icon)}>{getIcon()}</span>
      <span className={css(styles.text)}>{getText()}</span>
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    padding: '4px 8px',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
  },
  icon: {
    fontSize: '12px',
  },
  text: {
    whiteSpace: 'nowrap',
  },
  idle: {
    color: '#6B7280',
    backgroundColor: 'transparent',
  },
  saving: {
    color: '#1C64F2',
    backgroundColor: '#EBF5FF',
  },
  saved: {
    color: '#059669',
    backgroundColor: '#ECFDF5',
  },
  error: {
    color: '#DC2626',
    backgroundColor: '#FEF2F2',
  },
})

export default AutoSaveIndicator
